import React, { useRef, useState } from 'react';
import { authHeaderOrEmpty } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 10 * 1024 * 1024;

const baremePublicUrl = (storedUrl) => {
  if (!storedUrl) return null;
  if (/^https?:\/\//i.test(storedUrl)) return storedUrl;
  return `${API_URL}/api/public/agence-bareme/${storedUrl.split('/').pop()}`;
};

/**
 * Barème d'honoraires de l'agence — PDF uploadé (priorité) ou URL du site.
 * Le lien « Barème d'honoraires du cabinet » de la fiche publique consomme le PDF
 * s'il existe, sinon l'URL, sinon n'apparaît pas.
 */
export const AgencyBaremeCard = ({ agence, onSaved }) => {
  const storedFile = agence?.bareme_honoraires_file_url || null;
  const storedUrl = (agence?.links || []).find((l) => l.type === 'bareme_honoraires')?.url || '';
  const [url, setUrl] = useState(storedUrl);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const inputRef = useRef(null);

  const authHeaders = () => authHeaderOrEmpty();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.type !== 'application/pdf') {
      setFeedback({ type: 'error', text: 'Format invalide : le barème doit être un fichier PDF.' });
      return;
    }
    if (file.size > MAX_BYTES) {
      setFeedback({ type: 'error', text: `Fichier trop volumineux (${(file.size / 1048576).toFixed(1)} Mo) — maximum 10 Mo.` });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`${API_URL}/api/agences/${agence.id}/bareme-honoraires`, {
        method: 'POST', headers: authHeaders(), body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur lors de l'upload");
      onSaved?.({ bareme_honoraires_file_url: data.bareme_honoraires_file_url });
      setFeedback({ type: 'success', text: 'Barème enregistré. Il est désormais accessible depuis vos fiches publiques.' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async () => {
    if (!window.confirm('Supprimer le PDF du barème ?')) return;
    setBusy(true);
    setFeedback(null);
    try {
      const res = await fetch(`${API_URL}/api/agences/${agence.id}/bareme-honoraires`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur lors de la suppression');
      onSaved?.({ bareme_honoraires_file_url: null });
      setFeedback({ type: 'success', text: 'PDF supprimé.' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const saveUrl = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const res = await fetch(`${API_URL}/api/my-agence/bareme-honoraires-url`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur lors de la sauvegarde');
      onSaved?.({ links: data.links });
      setFeedback({ type: 'success', text: url.trim() ? 'URL enregistrée.' : 'URL supprimée.' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const active = storedFile ? 'PDF hébergé par Komerces' : (storedUrl ? 'Lien vers votre site' : 'Aucun barème');

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" data-testid="agence-bareme-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Barème d'honoraires</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Affiché sur vos fiches publiques à côté de la mention « Honoraires à la charge de… ».
            Le PDF est prioritaire sur l'URL.
          </p>
        </div>
        <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600" data-testid="agence-bareme-status">
          {active}
        </span>
      </div>

      {/* PDF */}
      <div className="rounded-xl border border-slate-200 p-4 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <i className={`bi ${storedFile ? 'bi-file-earmark-pdf-fill text-red-500' : 'bi-file-earmark-pdf text-slate-300'} text-2xl`} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">Document PDF</p>
              {storedFile ? (
                <a href={baremePublicUrl(storedFile)} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline break-all" data-testid="agence-bareme-file-link">
                  Ouvrir le barème actuel
                </a>
              ) : (
                <p className="text-xs text-slate-400">Aucun PDF — PDF uniquement, 10 Mo maximum</p>
              )}
            </div>
          </div>
          <div className="w-full md:w-auto flex items-center gap-2">
            <label
              className={`w-full md:w-auto justify-center px-3 h-11 md:h-auto md:py-2 rounded-lg font-medium text-sm flex items-center gap-2 cursor-pointer transition ${busy ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}`}
              data-testid="agence-bareme-upload-btn"
            >
              <i className="bi bi-cloud-arrow-up" />
              {storedFile ? 'Remplacer le PDF' : 'Uploader le PDF'}
              <input ref={inputRef} type="file" accept="application/pdf" onChange={upload} disabled={busy}
                className="hidden" data-testid="agence-bareme-upload-input" />
            </label>
            {storedFile && (
              <button onClick={removeFile} disabled={busy}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                title="Supprimer le PDF" data-testid="agence-bareme-delete-btn">
                <i className="bi bi-trash" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* URL */}
      <div className="rounded-xl border border-slate-200 p-4">
        <label className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1.5">
          Ou URL sur votre site
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.mon-agence.fr/bareme-honoraires.pdf"
            className="w-full md:flex-1 md:min-w-[260px] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            data-testid="agence-bareme-url-input"
          />
          <button
            onClick={saveUrl}
            disabled={busy || url.trim() === storedUrl}
            className={`w-full md:w-auto px-4 h-11 md:h-auto md:py-2 rounded-lg font-medium text-sm transition ${busy || url.trim() === storedUrl ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-slate-800 hover:bg-slate-900 text-white'}`}
            data-testid="agence-bareme-url-save-btn"
          >
            Enregistrer l'URL
          </button>
        </div>
        {storedFile && (
          <p className="text-xs text-amber-600 mt-2">
            Un PDF est en ligne : c'est lui qui est utilisé. Supprimez-le pour revenir à l'URL.
          </p>
        )}
      </div>

      {feedback && (
        <p className={`text-sm mt-4 ${feedback.type === 'error' ? 'text-red-600' : 'text-emerald-600'}`}
          data-testid="agence-bareme-feedback">
          <i className={`bi ${feedback.type === 'error' ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} mr-1.5`} />
          {feedback.text}
        </p>
      )}
    </div>
  );
};

export default AgencyBaremeCard;
